import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import useChat from '../../hooks/useChat';
import useAuth from '../../hooks/useAuth';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import ForkModal from './ForkModal';
import VoiceSettingsModal from './VoiceSettingsModal';
import SessionList from './SessionList';
import styles from './ChatPane.module.css';

const MODES = [
  { key: 'standard', label: 'standard', activeClass: styles.modePillStandard },
  { key: 'sparring', label: 'sparring', activeClass: styles.modePillSparring },
  { key: 'sympathetic', label: 'sympathetic', activeClass: styles.modePillSympathetic },
  { key: 'creative', label: 'creative', activeClass: styles.modePillCreative },
  { key: 'strategic', label: 'strategic', activeClass: styles.modePillStrategic },
  { key: 'presentation', label: 'presentation', activeClass: styles.modePillPresentation },
];

export default function ChatPane() {
  const { session } = useAuth();
  const {
    isOpen,
    messages,
    sessions,
    currentSessionId,
    isStreaming,
    mode,
    contextUsage,
    starters,
    freeMessagesUsed,
    isAtLimit,
    isAnonymous,
    FREE_MESSAGE_LIMIT,
    toggleChat,
    sendMessage,
    setMode,
    forkSession,
    renameSession,
    deleteSession,
    loadSessions,
    loadSession,
    startNewSession,
  } = useChat();
  const isAdmin = !!session;
  const messagesEndRef = useRef(null);
  const hasLoadedSessions = useRef(false);
  const [showForkModal, setShowForkModal] = useState(false);
  const [showVoiceSettings, setShowVoiceSettings] = useState(false);
  const [showSessionList, setShowSessionList] = useState(false);

  // Load sessions when chat opens (admin only)
  useEffect(() => {
    if (isOpen && isAdmin && !hasLoadedSessions.current) {
      hasLoadedSessions.current = true;
      loadSessions();
    }
  }, [isOpen, isAdmin, loadSessions]);

  // Lock body scroll when chat pane is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isOpen]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Auto-close session list when streaming starts
  useEffect(() => {
    if (isStreaming) setShowSessionList(false);
  }, [isStreaming]);

  if (!isOpen) return null;

  const contextPercent = contextUsage
    ? Math.min((contextUsage.inputTokens / contextUsage.contextWindow) * 100, 100)
    : 0;

  // Show counter on messages 4 and 5
  const showCounter = isAnonymous && freeMessagesUsed >= FREE_MESSAGE_LIMIT - 2 && !isAtLimit;
  const remaining = FREE_MESSAGE_LIMIT - freeMessagesUsed;

  return (
    <div className={styles.pane}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          {isAdmin && (
            <>
              <button
                className={styles.headerBtn}
                onClick={() => setShowSessionList(prev => !prev)}
                title={showSessionList ? 'Back to chat' : 'Conversations'}
              >
                {showSessionList ? '\u2190' : '\u2630'}
              </button>
              <button className={styles.headerBtn} onClick={() => setShowVoiceSettings(true)} title="Settings">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
              </button>
              {showSessionList && (
                <span className={styles.headerLabel}>Conversations</span>
              )}
            </>
          )}
        </div>
        <div className={styles.headerActions}>
          {isAdmin && !showSessionList && currentSessionId && !isStreaming && (
            <button
              className={styles.headerBtn}
              onClick={() => setShowForkModal(true)}
              title="Fork conversation"
            >
              &#x2442;
            </button>
          )}
          {isAdmin && (
            <button className={styles.headerBtn} onClick={() => { startNewSession(); setShowSessionList(false); }} title="New chat">+</button>
          )}
          <button className={styles.headerBtn} onClick={toggleChat} title="Close">&times;</button>
        </div>
      </div>

      {/* Mode selector — admin only, hidden when session list is open */}
      {isAdmin && !showSessionList && (
        <div className={styles.modeSelector} role="radiogroup" aria-label="Chat mode">
          {MODES.map(m => (
            <button
              key={m.key}
              className={`${styles.modePill} ${mode === m.key ? `${styles.modePillActive} ${m.activeClass}` : ''}`}
              role="radio"
              aria-checked={mode === m.key}
              onClick={() => setMode(m.key)}
              disabled={isStreaming}
            >
              {m.label}
            </button>
          ))}
        </div>
      )}

      {/* Context usage bar — admin only */}
      {isAdmin && !showSessionList && contextUsage && (
        <div className={styles.contextBar}>
          <div className={styles.contextBarFill} style={{ width: `${contextPercent}%` }} />
        </div>
      )}

      {isAdmin && showSessionList ? (
        <SessionList
          sessions={sessions}
          currentSessionId={currentSessionId}
          onSelectSession={loadSession}
          onNewSession={startNewSession}
          onRenameSession={renameSession}
          onDeleteSession={deleteSession}
          onClose={() => setShowSessionList(false)}
        />
      ) : (
        <>
          <div className={styles.messagesContainer}>
            {messages.length === 0 && (
              <div className={styles.emptyState}>
                {starters.length > 0 ? (
                  <div className={styles.starters}>
                    {starters.map((s, i) => (
                      <button
                        key={i}
                        className={styles.starterBtn}
                        onClick={() => sendMessage(s.text)}
                        disabled={isStreaming || isAtLimit}
                      >
                        {s.text}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p>What are you thinking about?</p>
                )}
              </div>
            )}
            {messages.map(msg => (
              <ChatMessage key={msg.id} message={msg} />
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Free message counter — shown above input on messages 4 and 5 */}
          {showCounter && (
            <div className={`${styles.freeCounter} ${remaining === 1 ? styles.freeCounterUrgent : ''}`}>
              {remaining} free message{remaining !== 1 ? 's' : ''} remaining
              <span className={styles.freeCounterSep}>&middot;</span>
              <Link to="/signup" className={styles.freeCounterLink}>Sign in</Link>
            </div>
          )}

          {/* Sign-up gate — replaces input when anonymous user hits limit */}
          {isAtLimit ? (
            <div className={styles.signupGate}>
              <p className={styles.signupGateText}>
                You've used your {FREE_MESSAGE_LIMIT} free messages. Sign up to keep the conversation going.
              </p>
              <div className={styles.signupGateActions}>
                <Link to="/signup" className={styles.signupGateBtn}>Sign up</Link>
                <Link to="/login" className={styles.signupGateLink}>Log in</Link>
              </div>
            </div>
          ) : (
            <ChatInput onSend={sendMessage} disabled={isStreaming} />
          )}
        </>
      )}

      {showForkModal && (
        <ForkModal
          onFork={(text) => {
            setShowForkModal(false);
            forkSession(text);
          }}
          onClose={() => setShowForkModal(false)}
        />
      )}

      {showVoiceSettings && (
        <VoiceSettingsModal onClose={() => setShowVoiceSettings(false)} />
      )}
    </div>
  );
}
